/**
 * Reolink NVR catalog — RLN / NVS series + Home Hub.
 *
 * All specs verified against reolink.com product pages on 2026-05-15.
 * URL cited per entry. Missing specs left undefined (no `raidLevels`
 * on any RLN/NVS — Reolink doesn't publish per-product RAID).
 *
 * RLN8-410, RLN16-410, NVS8, NVS16 ship with built-in PoE ports —
 * `poeInPorts` + `poePerPortW` populated. RLN36 / NVS36 have no
 * built-in PoE.
 *
 * Home Hub / Home Hub Pro are Wi-Fi 6 hubs intended for Reolink's
 * battery cameras — they do NOT support ONVIF (proprietary Reolink
 * protocol only).
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const REOLINK_NVR_CATALOG: EquipmentCatalogEntry[] = [
  {
    id: 'reolink-rln8-410',
    manufacturer: 'Reolink',
    model: 'RLN8-410',
    category: 'nvr-recorder',
    tagline: '8-channel PoE NVR with built-in PoE and eSATA expansion',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rln8-410/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '16 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 100M RJ45 (LAN/WAN)',
      poeInPorts: 8,
    },
  },

  {
    id: 'reolink-rln16-410',
    manufacturer: 'Reolink',
    model: 'RLN16-410',
    category: 'nvr-recorder',
    tagline: '16-channel PoE NVR with dual SATA bays for 24/7 recording',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rln16-410/',
    specs: {
      channels: 16,
      hddBays: 2,
      hddBayMaxTb: 8,
      maxResolution: '16 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 100M RJ45 (LAN/WAN)',
      poeInPorts: 16,
    },
  },

  {
    id: 'reolink-nvs8',
    manufacturer: 'Reolink',
    model: 'NVS8',
    category: 'nvr-recorder',
    tagline: '8-channel PoE NVR supporting up to 12 cameras incl. battery Wi-Fi',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/nvs8/',
    specs: {
      channels: 8,
      hddBays: 1,
      hddBayMaxTb: 8,
      maxResolution: '16 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 100M RJ45 (LAN/WAN)',
      poeInPorts: 8,
    },
  },

  {
    id: 'reolink-nvs16',
    manufacturer: 'Reolink',
    model: 'NVS16',
    category: 'nvr-recorder',
    tagline: '16-channel PoE NVR supporting up to 24 cameras including battery Wi-Fi',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/nvs16/',
    specs: {
      channels: 16,
      hddBays: 2,
      hddBayMaxTb: 8,
      maxResolution: '16 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 100M RJ45 (LAN/WAN)',
      poeInPorts: 16,
    },
  },

  {
    id: 'reolink-nvs36',
    manufacturer: 'Reolink',
    model: 'NVS36',
    category: 'nvr-recorder',
    tagline: '36-channel gigabit NVR with 48 TB capacity (no built-in PoE)',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/nvs36/',
    specs: {
      channels: 36,
      hddBays: 3,
      hddBayMaxTb: 16,
      maxResolution: '16 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '4× 1G RJ45 (cameras) + 1× 1G RJ45 (LAN/WAN)',
    },
  },

  {
    id: 'reolink-rln36',
    manufacturer: 'Reolink',
    model: 'RLN36',
    category: 'nvr-recorder',
    tagline: '36-channel gigabit NVR with 48 TB capacity for large installations',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rln36/',
    specs: {
      channels: 36,
      hddBays: 3,
      hddBayMaxTb: 16,
      maxResolution: '16 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '4× 1G RJ45 (cameras) + 1× 1G RJ45 (LAN/WAN)',
    },
  },

  {
    id: 'reolink-home-hub-pro',
    manufacturer: 'Reolink',
    model: 'Home Hub Pro',
    category: 'nvr-recorder',
    tagline: 'Wi-Fi 6 hub for up to 24 Reolink cameras including battery cams',
    verified: true,
    datasheetUrl: 'https://reolink.com/us/product/reolink-home-hub-pro/',
    specs: {
      channels: 24,
      hddBays: 1,
      hddBayMaxTb: 16,
      maxResolution: '16 MP',
      onvif: false,
      formFactor: 'desktop',
      networkPorts: '1× 100M WAN + 1× 100M cameras, Wi-Fi 6',
    },
  },

  {
    id: 'reolink-home-hub',
    manufacturer: 'Reolink',
    model: 'Home Hub',
    category: 'nvr-recorder',
    tagline: 'Compact Wi-Fi 6 hub with dual microSD slots for up to 8 cameras',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/reolink-home-hub/',
    specs: {
      channels: 8,
      hddBays: 0,
      maxResolution: '16 MP',
      onvif: false,
      formFactor: 'desktop',
      networkPorts: '1× 100M WAN, Wi-Fi 6',
    },
  },
];
